import React, { useState } from 'react';
import { Form, Input, Card, Space, Button, Select } from 'antd';
import { toast } from 'react-toastify';

import { ApiError, DeviceType, SuperGroup } from '@services/types';
import api from '@services/api';
import { useHistory, useParams } from 'react-router-dom';
import { Navigation } from '@constants/Navigation';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { LoadingOutlined } from '@ant-design/icons';
import { selectSearchFilter, validateDigitsAndLetters } from '@services/utils';
const { Option } = Select;

const CreateDeviceTypeModal = () => {
  const [form] = Form.useForm<DeviceType>();
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const [deviceType, setDeviceType] = React.useState<any>();
  const [superGroups, setSuperGroups] = React.useState<SuperGroup[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  React.useEffect(() => {
    if (id) {
      api.administration.getDeviceType(id).then((data) => {
        setDeviceType(data);
      });
    } else {
      api.administration.getSuperGroups().then((data) => {
        setSuperGroups(data);
      });
    }
  }, []);

  const onClose = () => {
    history.push(Navigation.DeviceType);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();
      if (id) {
        await api.administration.updateDeviceType({ ...values, id: id });
        toast.success('Device Type was updated successfully');
      } else {
        await api.administration.createDeviceType(values);
        toast.success('Device Type was created successfully');
      }
      form.resetFields();
      onClose();
    } catch (error) {
      if (error instanceof ApiError) {
        const errorMessage = error.error.details || Object.values(error.error.propertyErrors ?? {}).join(', ');
        toast.error(errorMessage);
      } else {
        toast.error('Device Type was not updated/created due to error');
      }
      console.log('CreateDeviceTypeModal ~ handleSubmit ~ error', error);
    } finally {
      setLoading(false);
    }
  };

  return deviceType ? (
    <Card
      title="Update an device type"
      extra={
        <Space>
          <Button icon={<ArrowLeftOutlined />} type="default" onClick={onClose}>
            Back to Device Type Page
          </Button>
          <Button type="primary" onClick={handleSubmit}>
            Update
          </Button>
        </Space>
      }
    >
      <Form form={form} layout="vertical" name="form_in_modal" initialValues={deviceType}>
        <Form.Item
          name="hardwareId"
          label="Hardware Id"
          rules={[
            {
              required: true,
              message: 'Please input the hardware id of device type!',
            },
          ]}
        >
          <Input disabled={!!deviceType} />
        </Form.Item>
        <Form.Item
          name="usageId"
          label="Usage Id"
          rules={[
            {
              required: true,
              message: 'Please input the usage id of device type!',
            },
          ]}
        >
          <Input disabled={!!deviceType} />
        </Form.Item>
        <Form.Item name="rescueName" label="Rescue Name">
          <Input />
        </Form.Item>
        <Form.Item
          name="deviceName"
          label="Device Type Name"
          rules={[
            {
              required: true,
              message: 'Please input the name of device type!',
            },
          ]}
        >
          <Input />
        </Form.Item>
      </Form>
    </Card>
  ) : !id ? (
    <Card
      title="Create a new device type"
      extra={
        <Space>
          <Button icon={<ArrowLeftOutlined />} type="default" onClick={onClose}>
            Back to Device Type Page
          </Button>
          <Button type="primary" onClick={handleSubmit}>
            Create
          </Button>
        </Space>
      }
    >
      <Form
        form={form}
        layout="vertical"
        name="form_in_modal"
        initialValues={{
          hardwareId: '',
          usageId: '',
          name: '',
          rescueName: '',
          scopeId: '',
        }}
      >
        <Form.Item
          name="hardwareId"
          label="Hardware Id"
          rules={[
            {
              validator: validateDigitsAndLetters,
              required: true,
              message: 'Please enter letters and digits only for the device type hardware ID!',
            },
          ]}
        >
          <Input disabled={!!deviceType} />
        </Form.Item>
        <Form.Item
          name="usageId"
          label="Usage Id"
          rules={[
            {
              validator: validateDigitsAndLetters,
              required: true,
              message: 'Please enter letters and digits only for the device type usage ID!',
            },
          ]}
        >
          <Input disabled={!!deviceType} />
        </Form.Item>
        <Form.Item name="rescueName" label="Rescue Name">
          <Input />
        </Form.Item>
        <Form.Item
          name="deviceName"
          label="Device Type Name"
          rules={[
            {
              required: true,
              message: 'Please input the name of device type!',
            },
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="scopeId"
          label="Scope"
          rules={[
            {
              required: true,
              message: 'Please select the scope',
            },
          ]}
        >
          <Select allowClear showSearch filterOption={selectSearchFilter}>
            {superGroups?.map((item) => (
              <Option key={item.id} value={item.id}>
                {item.name}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Form>
      {!deviceType && loading && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'max-content max-content',
            gridGap: '10px',
            alignItems: 'center',
            paddingTop: '5px',
          }}
        >
          <span>Please wait, a device type is being created...</span>
          <LoadingOutlined />
        </div>
      )}
    </Card>
  ) : null;
};

export default React.memo(CreateDeviceTypeModal);
